import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EditableDamageAttributes from 'components/damages/EditableDamageAttributes';
import { type DamageEditPayload } from 'components/damages/EditableDamageAttributes/types';
import { useServerDataTableContext } from 'components/DataTable/ServerDataTable';
import { Spinner, Tooltip } from 'components/ui';
import { getPrimaryObservationForObservationGroup } from 'horizon/components/Damages/utils';
import {
  type AtlasGqlHorizonDamageObservationGroup,
  useUpdateHorizonDamageObservationGroupMutation,
} from 'types/atlas-graphql';
import { usePotentialDamageAttrs } from 'utils/usePotentialDamageAttrs';

import { StyledAlert, StyledTitle } from './EditAttributesForm.style';

type EditAttributesFormProps = {
  backgroundColor?: string;
  damageId: string;
  selectedSchema?: string;
  observationGroup: AtlasGqlHorizonDamageObservationGroup;
  onCancel: () => void;
  showLoadingSpinner?: boolean;
  onSavingChange?: (saving: boolean) => void;
  onSaveDisabledChange?: (disabled: boolean) => void;
};

export const EditAttributesForm: React.FunctionComponent<EditAttributesFormProps> = ({
  backgroundColor,
  selectedSchema,
  observationGroup,
  onCancel,
  showLoadingSpinner = false,
  onSavingChange,
  onSaveDisabledChange,
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [editAttrs, setEditAttrs] = useState<Record<string, any>>({});

  useEffect(() => {
    onSavingChange?.(submitting);
  }, [submitting, onSavingChange]);

  const { refetch: refetchDamageTable, tableFilters: damageTableFilters } =
    useServerDataTableContext().context;

  const groupAttrs = observationGroup?.groupAttrs as any;
  const groupCritical = observationGroup?.groupCritical as boolean | undefined;

  const observationsInGroup = observationGroup.observations ?? [];
  const primaryObservation = getPrimaryObservationForObservationGroup(observationGroup);

  // do not pass attrs, since this is always editing an existing observation
  const { potentialAttrs, loading: potentialAttrsLoading } = usePotentialDamageAttrs({
    pictureId: primaryObservation?.picture?.id,
    coordinates: primaryObservation?.coordinates ?? [],
  });

  const [updateObservationGroup] = useUpdateHorizonDamageObservationGroupMutation({
    onCompleted: () => {
      setSubmitting(false);

      if (refetchDamageTable && !!damageTableFilters?.length) {
        refetchDamageTable();
      }

      onCancel();
    },
    update: (cache, { data }) => {
      const updatedGroup = data?.updateHorizonDamageObservationGroup;
      if (
        updatedGroup?.isPrimaryObservationGroup === true &&
        updatedGroup?.damageId &&
        updatedGroup?.groupAttrs
      ) {
        cache.modify({
          id: `HorizonDamage:${updatedGroup.damageId}`,
          fields: {
            primaryObservationGroupAttrs(_currentAttrs) {
              return updatedGroup.groupAttrs;
            },
          },
        });
      }
    },
  });

  const handleAttrsChange = useCallback((_attrs: Record<string, any>) => {
    setEditAttrs(_attrs);
  }, []);

  const handleSave = useCallback(
    async (payload: DamageEditPayload) => {
      const { attrs = [], critical } = payload;

      setSubmitting(true);
      try {
        await updateObservationGroup({
          variables: {
            input: {
              id: observationGroup.id,
              groupAttrs: Object.fromEntries(
                attrs.map(({ name, string, number, date }) => [name, string ?? number ?? date])
              ),
              groupCritical: critical,
            },
          },
        });
      } catch (error) {
        setSubmitting(false);
        throw error;
      }
    },
    [observationGroup, updateObservationGroup]
  );

  return (
    <>
      {observationsInGroup.length > 1 && (
        <Tooltip
          title={
            <StyledTitle>
              <>
                <div>
                  {t(
                    'damage.observationGroupAttributesCoverAll',
                    'Observation Group attributes cover all Observations linked from one inspection. Editing Observation Group attributes will not change Observation attributes.'
                  )}
                </div>
                <div>
                  {t(
                    'damage.observationGroupAttributesExample',
                    'Example: A Damage captured on adjoining photos as two Severity 3 Observations may warrant a Severity of 4 when considered together.'
                  )}
                </div>
              </>
            </StyledTitle>
          }
        >
          <StyledAlert
            message={t(
              'damage.editingObservationGroupAttributes',
              'Editing Observation Group Attributes'
            )}
            type="warning"
            showIcon
          />
        </Tooltip>
      )}
      {showLoadingSpinner && potentialAttrsLoading ? (
        <Spinner />
      ) : (
        <EditableDamageAttributes
          schemaId={selectedSchema}
          damage={{ attrs: groupAttrs }}
          critical={groupCritical}
          editAttrs={editAttrs}
          potentialAttrs={potentialAttrs}
          potentialAssets={{ likely: [], nearby: [] }}
          isNewObservation={false}
          canChangeAsset={false}
          canChangeSchema={false}
          loading={submitting || potentialAttrsLoading}
          shouldHideRealtimePotentialAttrs={observationsInGroup.length > 1}
          shapeDrawn
          shapeChanged={false}
          onCancel={onCancel}
          onSave={handleSave}
          onAttrsChange={handleAttrsChange}
          onSaveDisabledChange={onSaveDisabledChange}
          showComment={false}
          ignoreWorkInProgress
          backgroundColor={backgroundColor}
        />
      )}
    </>
  );
};
